import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { api } from "../lib/api";
import { DEFAULT_REVIEWS } from "../data";

const Stars = ({ n }) => (
  <div className="flex gap-1 mb-5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={16} className={i <= n ? "fill-gold text-gold" : "text-white/20"} />
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState(DEFAULT_REVIEWS);

  useEffect(() => {
    api.get("/reviews", { params: { limit: 6 } })
      .then((r) => {
        const real = r.data || [];
        // Show real reviews first; top up with curated ones so the section never looks empty.
        const merged = [...real];
        for (const d of DEFAULT_REVIEWS) {
          if (merged.length >= 3) break;
          if (!merged.some((m) => m.comment === d.comment)) merged.push(d);
        }
        setReviews(merged.slice(0, 6));
      })
      .catch(() => setReviews(DEFAULT_REVIEWS.slice(0, 3)));
  }, []);

  return (
    <section id="reviews" className="relative py-32 md:py-48 bg-[var(--nl-bg-2)]" data-testid="reviews">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="label text-gold mb-6"
        >
          Kind Words
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight mb-16 max-w-3xl"
        >
          Trusted by Ontario's <span className="italic text-gold">most discerning</span> travellers.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((rv, i) => (
            <motion.div
              key={`${rv.name}-${i}`}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative border border-white/10 bg-[var(--nl-bg-3)] p-8 flex flex-col"
              data-testid={`review-${i}`}
            >
              <Quote size={28} className="text-gold/30 mb-4" />
              <Stars n={rv.rating} />
              <p className="font-serif font-light text-lg md:text-xl leading-relaxed text-white/80 italic mb-6 flex-1">
                "{rv.comment}"
              </p>
              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <span className="font-body text-sm text-white">{rv.name}</span>
                {rv.service && <span className="label text-[10px] text-gold">{rv.service}</span>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
